// ADR 23.9.26v §2 — the last adopted identity, kept so an offline launch can
// resume a session whose refresh never reached the server.
//
// IndexedDB (§3's database, key `session`), not `localStorage`: the refresh key
// is pinned as the token store's only `localStorage` key (5.9.26p AC5), and the
// flags are the structured value IndexedDB exists for. Bounded by the cache's
// own `MAX_AGE_MS` — one bound serves both, and it is far shorter than the
// refresh token's server lifetime. Purged with the cache on sign-out.

import type { FeatureFlags } from '../api/auth'
import { offlineDb, type OfflineKv } from './offlineDb'
import { MAX_AGE_MS } from './persistPolicy'

export const SESSION_SNAPSHOT_KEY = 'session'

export interface SessionSnapshot {
  userId: string
  emailVerified: boolean
  flags: FeatureFlags
  savedAtMs: number
}

/** The snapshot, or `null` when there is none or it is older than `MAX_AGE_MS`. */
export async function readSessionSnapshot(
  store: OfflineKv = offlineDb,
  now: number = Date.now(),
): Promise<SessionSnapshot | null> {
  const snapshot = await store.get<SessionSnapshot>(SESSION_SNAPSHOT_KEY)
  if (snapshot === undefined || now - snapshot.savedAtMs > MAX_AGE_MS) return null
  return snapshot
}

/**
 * Fire-and-forget, never awaited: an IndexedDB stall must not delay a sign-in.
 * The store's own guard means it never rejects.
 */
export function saveSessionSnapshot(
  snapshot: Omit<SessionSnapshot, 'savedAtMs'>,
  store: OfflineKv = offlineDb,
): void {
  void store.set(SESSION_SNAPSHOT_KEY, { ...snapshot, savedAtMs: Date.now() })
}

export function clearSessionSnapshot(store: OfflineKv = offlineDb): Promise<void> {
  return store.del(SESSION_SNAPSHOT_KEY)
}
